/**
 * RequireKyc — gate privileged routes on KYC completion.
 *
 * Composes inside `<ProtectedRoute>` (auth) and (optionally)
 * `<RequireMandatoryConsent>` (DPDP). On first authenticated render
 * it fetches the user's KYC + docs + bank state and decides whether
 * the user has met every mandatory requirement for their role.
 *
 * If anything's missing it redirects to `/kyc-onboarding` with the
 * original location stashed so the wizard can return them after.
 *
 * Roles gated:
 *  - HOST              — required: PAN + Aadhaar only. GSTIN, bank
 *                        and trade certificates are *optional* — a
 *                        host can list a warehouse without them.
 *                        GSTIN / bank still appear in the wizard so
 *                        the host can complete them later (GSTIN
 *                        unlocks GST-compliant invoicing; bank
 *                        unlocks payouts) but they don't gate
 *                        listing creation.
 *  - LOGISTICS_PARTNER — required (PAN, Aadhaar, GSTIN, bank, DL, RC)
 *  - TENANT            — KYC is optional at the gate level; specific
 *                        actions (booking, payouts) gate themselves
 *                        per-feature.
 *  - ADMIN / STAFF     — not gated (they have their own auth path).
 *
 * The decision is fail-open on network failure so a transient
 * compliance-service blip never bricks your dashboard. The next
 * privileged-action gate re-checks anyway.
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spinner } from 'reactstrap';
import consentService from '../../services/consentService';
import { isOnboardingComplete, stepsForRole } from './kycRequirements';

const ROLES_THAT_REQUIRE_KYC = new Set(['HOST', 'LOGISTICS_PARTNER']);

// Pages a partly-onboarded user may reach without completing KYC. Kept
// module-level (and exported) so the KYC wizard's "Finish later" can send
// the user back to the page they were heading for when that page is safe.
export const PASSTHROUGH_PATHS = [
  '/kyc-onboarding',
  '/dashboard',
  '/profile',
  '/settings',
  // A partly-onboarded host can start adding a warehouse after clicking
  // "finish later" — previously the "Add Warehouse" button bounced straight
  // back to /kyc-onboarding (mobile already allows this). The listing stays
  // in DRAFT/pending review and won't auto-publish until KYC is verified, so
  // the per-action publish gate still protects go-live.
  '/warehouses/onboard',
  '/warehouses/new',
  // ...and must be able to SEE what they just added. "My Warehouses" and the
  // warehouse list/detail/edit pages are read-or-edit-your-own-draft surfaces;
  // publishing is still gated server-side by KYC, so nothing risky opens up.
  // Client-reported 2026-09-15: host added a warehouse, clicked "My
  // Warehouses" → KYC wizard → "finish later" → dashboard, warehouse unseen.
  '/my-warehouses',
  '/warehouses',
];

export const isKycPassthroughPath = (pathname) =>
  PASSTHROUGH_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

const RequireKyc = ({ children }) => {
  const { isAuthenticated, user } = useSelector((s) => s.auth);
  const location = useLocation();
  const [state, setState] = useState({ status: 'loading', complete: true });

  const role = String(user?.role || '').toUpperCase();
  const gated = ROLES_THAT_REQUIRE_KYC.has(role) && stepsForRole(role).length > 0;

  useEffect(() => {
    if (!isAuthenticated || !user || !gated) {
      setState({ status: 'ready', complete: true });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [kycRes, docsRes, bankRes, consentsRes] = await Promise.allSettled([
          consentService.listMyKyc(),
          consentService.listMyDocuments(),
          consentService.listBankAccounts(),
          consentService.listMine(),
        ]);
        if (cancelled) return;
        const snapshot = {
          kyc: kycRes.status === 'fulfilled' ? (kycRes.value.data?.items || []) : [],
          docs: docsRes.status === 'fulfilled' ? (docsRes.value.data?.items || docsRes.value.data || []) : [],
          bankAccounts: bankRes.status === 'fulfilled' ? (bankRes.value.data?.items || bankRes.value.data || []) : [],
          consents: consentsRes.status === 'fulfilled' ? (consentsRes.value.data?.consents || []) : [],
        };
        setState({
          status: 'ready',
          complete: isOnboardingComplete(role, snapshot),
        });
      } catch {
        // Fail-open — let the user reach the app.
        if (!cancelled) setState({ status: 'ready', complete: true });
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, user, gated, role]);

  // Don't gate the wizard itself, the dashboard, or the user's
  // own profile/settings — those are the safe-harbor pages a
  // partly-onboarded user should be able to land on without
  // bouncing. The dashboard renders a "finish KYC" banner; the
  // privileged actions (booking, payouts, listings) keep their
  // own gates so this passthrough doesn't open up anything risky.
  if (isKycPassthroughPath(location.pathname)) {
    return children;
  }

  if (!gated) return children;

  if (state.status === 'loading') {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '50vh' }}>
        <Spinner /> <span className="ms-2 text-muted">Loading…</span>
      </div>
    );
  }

  if (!state.complete) {
    return <Navigate to="/kyc-onboarding" state={{ from: location }} replace />;
  }

  return children;
};

export default RequireKyc;
